import * as I from 'interfaces';
import refreshHandler, {mutateCage, mutateSite, mutateTransfer} from 'helpers/refreshHandler';
import {type NavigationTreePlan, type NavigationTreeSite} from 'client';
import {type Dispatch} from 'react';
import {type ScopedMutator} from 'swr/_internal';
import {type NavigateFunction} from 'react-router-dom';
import {mutate as globalMutate} from 'swr';

const handleCommunicationSucces = ({
  type,
  navigate,
  pathname,
  planId,
  siteId,
  cageId,
  urlSearchParams,
  inputMenuDispatch,
  mutate,
  formData,
  treePlan,
  startDate,
  endDate,
  filteredSites,
}: IDeleteRedirect) => {
  inputMenuDispatch && inputMenuDispatch({type: I.inputMenuActionType.closePopOver});

  // Note(Alexander): remove sites and cages from cache without revalidating to avoid requesting 404s
  if (type === I.FormSelectionEnum.site && siteId) {
    void globalMutate(
      [planId, I.SWRKeyEnum.GET_PLAN_TREE_NAVIGATION],
      (currentData: NavigationTreePlan | undefined) => {
        if (!currentData) {
          return currentData;
        }

        return {...currentData, sites: currentData.sites?.filter((site) => site.id !== siteId)};
      },
      {revalidate: false},
    );

    void globalMutate(
      [planId, I.SWRKeyEnum.GET_PLAN_SITES],
      (currentData: any[] | undefined) => currentData?.filter((site) => site.id !== siteId),
      {revalidate: false},
    );

    void globalMutate((key) => Array.isArray(key) && key.includes(siteId), undefined, {revalidate: false});
  }

  if (type === I.FormSelectionEnum.cage && cageId) {
    void globalMutate(
      [planId, I.SWRKeyEnum.GET_PLAN_TREE_NAVIGATION],
      (currentData: NavigationTreePlan | undefined) => {
        if (!currentData?.sites) {
          return currentData;
        }
        return {
          ...currentData,
          sites: currentData.sites.map((site) => ({
            ...site,
            cages: site.cages?.filter((cage) => cage.id !== cageId),
          })),
        };
      },
      {revalidate: false},
    );

    if (siteId) {
      void globalMutate(
        [siteId, I.SWRKeyEnum.GET_SITE_CAGES],
        (currentData: any[] | undefined) => currentData?.filter((cage) => cage.id !== cageId),
        {revalidate: false},
      );
    }

    void globalMutate((key) => Array.isArray(key) && key.includes(cageId), undefined, {revalidate: false});
  }

  if (type === I.FormSelectionEnum.plan) {
    // TODO: add refresh handler
    navigate && navigate('/', {replace: true});
  } else if (type === I.FormSelectionEnum.site || type === I.FormSelectionEnum.cage) {
    mutate([planId, I.SWRKeyEnum.GET_PLAN_TREE_NAVIGATION]);
    if (type === I.FormSelectionEnum.site) {
      mutate([planId, I.SWRKeyEnum.GET_PLAN_SITES]);
    }
    if (siteId && type === I.FormSelectionEnum.cage) {
      mutate([siteId, I.SWRKeyEnum.GET_SITE_CAGES]);
    }
    urlSearchParams.delete(type);
    navigate &&
      navigate({
        pathname,
        search: urlSearchParams.toString(),
      });
  } else if (type === I.FormSelectionEnum.fallowing && formData?.siteId && treePlan) {
    // Update site cages forecaste
    const treeSite: NavigationTreeSite | undefined = treePlan.sites?.find((site) => site.id === siteId);
    treeSite?.cages?.forEach((cages) => {
      mutateCage(cages.id);
    });
    mutateSite(formData?.siteId);

    mutate([formData.siteId, I.SWRKeyEnum.GET_SITE_FALLOWINGS]);
  } else if (type === I.FormSelectionEnum.transfer && formData?.transfer) {
    const {fromSiteId, toSiteId, fromCageId, allocations} = formData.transfer;
    const cageIds = [fromCageId, ...allocations.map((allocation) => allocation.toCageId)];
    mutateTransfer(planId, fromSiteId, toSiteId, cageIds, startDate, endDate);
  } else if (type === I.FormSelectionEnum.smoltAvailability) {
    mutate([planId, I.SWRKeyEnum.GET_PLAN_SMOLT_SUPPLY]);
  } else if (type === I.FormSelectionEnum.input) {
    mutate([planId, I.SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
  }

  refreshHandler({
    planId,
    // Note(Alexander): don't call refreshHandler if deleting sites and cages to avoid 404s
    siteId: type === I.FormSelectionEnum.site ? null : siteId,
    cageId: type === I.FormSelectionEnum.cage ? null : cageId,
    startDate,
    endDate,
    filteredSites,
  });
};

export default handleCommunicationSucces;

interface IDeleteRedirect {
  type?: I.FormSelectionEnum;
  navigate?: NavigateFunction;
  inputMenuDispatch?: Dispatch<I.InputMenuAction>;
  pathname: string;
  urlSearchParams: URLSearchParams;
  planId: string;
  siteId?: string | null;
  cageId?: string | null;
  mutate: ScopedMutator;
  formData?: I.IFormData;
  treePlan?: NavigationTreePlan;
  startDate: Date;
  endDate: Date;
  filteredSites: string[];
}
